/**
 * Manager approval at the register (T07-18 step-up, design/05 §7): a cashier's request that needs a
 * manager comes back `403 manager_approval_required` with `details.{requiredPermission, action,
 * reason{ja,th,en}}` → the approval dialog explains why in one sentence → the manager taps their
 * name (or scans their card) and types the PIN → identity-service issues a one-use 2-minute token
 * → the same request is sent again with `X-Approval-Token`. Cancel = nothing changed.
 */
import { useLiveQuery } from "dexie-react-hooks";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { NumPad } from "@/components/NumPad";
import { type ApiError, errorText, isApiError } from "@/lib/errors";

export class ApprovalCancelled extends Error {
  constructor() {
    super("approval cancelled");
    this.name = "ApprovalCancelled";
  }
}

export interface ApprovalAsk {
  permission: string;
  action: string;
  subject: string;
  amount?: number | null;
  /** The one-sentence reason (server's `details.reason[locale]`). */
  reason: string;
}

interface Pending extends ApprovalAsk {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

export interface ApprovalApi {
  /** Run `send` (without a token); when a manager is needed, ask, then send again with the token. */
  withApproval<T>(
    send: (approvalToken?: string) => Promise<T>,
    context: { subject: string; amount?: number | null },
  ): Promise<T>;
  /** Ask the manager directly (the screen already knows approval is needed). */
  ask(request: ApprovalAsk): Promise<string>;
}

const ApprovalContext = createContext<ApprovalApi | null>(null);

/** pos-service reads at most 3 tokens from one `X-Approval-Token` header. */
const MAX_APPROVALS = 3;

function reasonOf(error: ApiError, locale: string, fallback: string): string {
  const reason = error.detail.reason;
  if (reason && typeof reason === "object") {
    const map = reason as Record<string, unknown>;
    const text = map[locale] ?? map.ja ?? map.en;
    if (typeof text === "string") {
      return text;
    }
  }
  return error.message || fallback;
}

export function ApprovalProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);

  const ask = useCallback((request: ApprovalAsk) => {
    return new Promise<string>((resolve, reject) => {
      const next: Pending = { ...request, resolve, reject };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const withApproval = useCallback(
    async <T,>(
      send: (approvalToken?: string) => Promise<T>,
      context: { subject: string; amount?: number | null },
    ) => {
      // One request may need more than one approval (closing a colleague's shift **and** a
      // large 過不足): each answer names the next action; the tokens so far go together,
      // comma-separated, and pos-service uses the one made for each action.
      const tokens: string[] = [];
      const asked = new Set<string>();
      for (;;) {
        try {
          return await send(tokens.length ? tokens.join(",") : undefined);
        } catch (error) {
          if (!isApiError(error, "manager_approval_required")) {
            throw error;
          }
          const permission = String(error.detail.requiredPermission ?? "");
          const action = String(error.detail.action ?? "");
          if (!permission || !action || asked.has(action) || tokens.length >= MAX_APPROVALS) {
            throw error;
          }
          asked.add(action);
          tokens.push(
            await ask({
              permission,
              action,
              subject: context.subject,
              amount: context.amount ?? null,
              reason: reasonOf(
                error,
                i18n.resolvedLanguage ?? "ja",
                t("errors.manager_approval_required"),
              ),
            }),
          );
        }
      }
    },
    [ask, i18n, t],
  );

  const api = useMemo<ApprovalApi>(() => ({ withApproval, ask }), [withApproval, ask]);

  const close = (result: { token: string } | { cancelled: true }) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    if (!current) {
      return;
    }
    if ("token" in result) {
      current.resolve(result.token);
    } else {
      current.reject(new ApprovalCancelled());
    }
  };

  return (
    <ApprovalContext.Provider value={api}>
      {children}
      {pending ? <ApprovalDialog ask={pending} onDone={close} /> : null}
    </ApprovalContext.Provider>
  );
}

export function useApproval(): ApprovalApi {
  const api = useContext(ApprovalContext);
  if (!api) {
    throw new Error("useApproval() outside <ApprovalProvider>");
  }
  return api;
}

function ApprovalDialog({
  ask,
  onDone,
}: {
  ask: ApprovalAsk;
  onDone: (result: { token: string } | { cancelled: true }) => void;
}) {
  const { t } = useTranslation();
  const { auth, db } = usePos();
  const recent = useLiveQuery(
    () => db.staffRecent.orderBy("lastSeenAt").reverse().limit(8).toArray(),
    [db],
  );
  const [staffId, setStaffId] = useState<string | null>(null);
  const [badge, setBadge] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (pin.length < 4 || (!staffId && !badge.trim())) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const grant = await auth.approve({
        ...(staffId ? { staffId } : { badge: badge.trim() }),
        pin,
        action: ask.action,
        permission: ask.permission,
        subject: ask.subject,
        amount: ask.amount ?? null,
      });
      onDone({ token: grant.approvalToken });
    } catch (err) {
      setPin("");
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={t("approval.title")}
      description={ask.reason}
      onClose={() => onDone({ cancelled: true })}
      busy={busy}
      footer={
        <button
          type="button"
          onClick={() => onDone({ cancelled: true })}
          disabled={busy}
          className="min-h-pos-touch rounded-md border px-6 text-lg"
        >
          {t("app.cancel")}
        </button>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-medium">{t("approval.manager")}</legend>
          {(recent ?? []).map((person) => (
            <button
              key={person.id}
              type="button"
              aria-pressed={staffId === person.id}
              onClick={() => {
                setStaffId(person.id);
                setBadge("");
              }}
              className="min-h-pos-touch rounded-md border px-4 text-left text-lg aria-pressed:border-primary aria-pressed:bg-accent"
            >
              {person.name}
            </button>
          ))}
          <label className="mt-2 flex flex-col gap-1 text-sm">
            {t("approval.manager_badge")}
            <input
              value={badge}
              onChange={(e) => {
                setBadge(e.target.value);
                setStaffId(null);
              }}
              autoComplete="off"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
        </fieldset>
        <div className="flex flex-col gap-2">
          <NumPad
            label={t("approval.pin")}
            value={pin}
            onChange={setPin}
            maxLength={6}
            masked
            disabled={busy}
            onEnter={() => void submit()}
            enterLabel={t("approval.approve")}
          />
          {error ? (
            <p role="alert" className="text-pos-problem">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
